import { Component, Input } from '@angular/core';

import { HisAuthService, HisNavigationService } from 'hisng';

import { OPEN_ACCESS, PEER_REVIEWED_HISKEY_ID, NONE_QUALIFICATION_THESIS } from '../../../model/constants';
import { DataDictionaryUtils } from 'app/fs/res/common/utils/data-dictionary-utils';
import { CreatorType } from '../../../model/enums/creator-type-enum';
import { GlobalIdentifierEnum } from '../../../model/enums/global-identifier-enum';
import { PublicationCreatorOrgunit } from '../../../model/publication-creator-orgunit';
import { Publication } from '../../../model/publication';
import { PublicationCreator } from '../../../model/publication-creator';

@Component( {
    selector: 'res-publication-view-details-vcard',
    templateUrl: './publication-view-details-vcard.component.html',
    preserveWhitespaces: false
} )
export class PublicationViewDetailsVCardComponent {
    @Input() publication: Publication;
    @Input() showParentLink: boolean;
    // Maps containing information regarding the visibility of links to other detail pages
    @Input() checkedPersonIdsForRights: Map<number, boolean>;

    readonly OPEN_ACCESS = OPEN_ACCESS;
    readonly PEER_REVIEWED_HISKEY_ID = PEER_REVIEWED_HISKEY_ID;
    readonly NONE_QUALIFICATION_THESIS = NONE_QUALIFICATION_THESIS;

    public identifierEnum: typeof GlobalIdentifierEnum = GlobalIdentifierEnum; // used in html
    public showAllCreators = false;

    constructor( public auth: HisAuthService,
        private navigationService: HisNavigationService ) { }

    public get pageRange() {
        let pageRange = '';
        if (this.publication.pageRangeFrom && this.publication.pageRangeTo) {
            pageRange = this.publication.pageRangeFrom + '-' + this.publication.pageRangeTo;
        } else if(this.publication.pageRangeFrom) {
            pageRange = this.publication.pageRangeFrom;
        } else if(this.publication.pageRangeTo) {
            pageRange = this.publication.pageRangeTo;
        }
        return pageRange;
    }

    public getDdKey( source: string ) {
        if ( this.publication && this.publication.publicationType ) {
            return DataDictionaryUtils.dDKeyPublicationType( this.publication.publicationType, source );
        }
        return undefined;
    }

    public isEmpty( input: any ): boolean {
        return !input || !input.length;
    }

    public isTypeOfEditor(): boolean {
        return this.publication.publicationCreatorType && CreatorType.editors().findIndex( c =>
            c === this.publication.publicationCreatorType.hiskeyId ) > -1;
    }

    public isTypeOfAuthor(): boolean {
        return this.publication.publicationCreatorType && CreatorType.authors().findIndex( c =>
            c === this.publication.publicationCreatorType.hiskeyId ) > -1;
    }

    public getTitleForCreatorLink(): string {
        if ( this.isTypeOfAuthor() ) {
            return 'fs.res.publication.author';
        } else if ( this.isTypeOfEditor() ) {
            return 'fs.res.publication.editor';
        }
        return undefined;
    }

    public getCreatorOrganizations( organizations: PublicationCreatorOrgunit[] ): string {
        return organizations.map( this.getCreatorOrganization ).join( ', ' );
    }

    public getCreatorFullName( creator: PublicationCreator ): string  {
        return creator.firstname.concat(' '
            + this.getNamePart( creator.nameprefix ), ' '
            + creator.creatorname, ' '
            + this.getNamePart( creator.namesuffix ) ).trim();
    }

    /* get part of creator name or empty string */
    private getNamePart( namePart: string ) {
        return  namePart !== null ? namePart : '';
    }

    private getCreatorOrganization( orgunit: PublicationCreatorOrgunit ) {
        if ( orgunit.organization ) {
            return orgunit.organization?.text;
        }
        return orgunit.researchPartner.defaulttext;
    }

    public showAsPerson( creator: PublicationCreator ): boolean {
        return !this.isEmpty( creator.firstname );
    }

    public showAsOrganization( creator: PublicationCreator ): boolean {
        return !!creator.creatorOrganizations[0]?.organization;
    }

    public showAsExternalOrganization( creator: PublicationCreator ): boolean {
        return !!creator.creatorOrganizations[0]?.researchPartner;
    }

    public showAsSomethingElse( creator: PublicationCreator ): boolean {
        let isSomethingElse =
            !( this.showAsPerson( creator ) ) &&
            !( this.showAsOrganization( creator ) ) &&
            !( this.showAsExternalOrganization( creator ) );
        if ( isSomethingElse ) {
            console.warn('cannot identify creator as either a person, an organization or an external organitation', creator );
        }
        return isSomethingElse;
    }

    /* leaving page action use onBeforeNavigate set last back url with navigationService */
    public onBeforeNavigate() {
        this.navigationService.setLastBackUrl(window.location.href);
    }

    public embargoDateReached() {
        return new Date(new Date(this.publication.embargoDate).toDateString()) <= new Date(new Date().toDateString());
    }
}
